import { useState } from "react";
import { motion } from "framer-motion";
import { toast } from "sonner";

const WEB3FORMS_URL = "https://api.web3forms.com/submit";
const ACCESS_KEY = process.env.REACT_APP_WEB3FORMS_KEY;
const initial = { name: "", email: "", countryCode: "+91", phone: "", company: "", message: "", website: "" };

const COUNTRY_CODES = [
  "+1", "+7", "+20", "+27", "+30", "+31", "+32", "+33", "+34", "+36", "+39", "+40", 
  "+41", "+43", "+44", "+45", "+46", "+47", "+48", "+49", "+51", "+52", "+53", "+54", 
  "+55", "+56", "+57", "+58", "+60", "+61", "+62", "+63", "+64", "+65", "+66", "+81", 
  "+82", "+84", "+86", "+90", "+91", "+92", "+93", "+94", "+95", "+98", "+212", "+213", 
  "+216", "+218", "+220", "+221", "+222", "+223", "+224", "+225", "+226", "+227", "+228", 
  "+229", "+230", "+231", "+232", "+233", "+234", "+235", "+236", "+237", "+238", "+239", 
  "+240", "+241", "+242", "+243", "+244", "+245", "+246", "+248", "+249", "+250", "+251", 
  "+252", "+253", "+254", "+255", "+256", "+257", "+258", "+260", "+261", "+262", "+263", 
  "+264", "+265", "+266", "+267", "+268", "+269", "+290", "+291", "+297", "+298", "+299", 
  "+350", "+351", "+352", "+353", "+354", "+355", "+356", "+357", "+358", "+359", "+370", 
  "+371", "+372", "+373", "+374", "+375", "+376", "+377", "+378", "+380", "+381", "+382", 
  "+383", "+385", "+386", "+387", "+389", "+420", "+421", "+423", "+500", "+501", "+502", 
  "+503", "+504", "+505", "+506", "+507", "+508", "+509", "+590", "+591", "+592", "+593", 
  "+594", "+595", "+596", "+597", "+598", "+599", "+670", "+672", "+673", "+674", "+675", 
  "+676", "+677", "+678", "+679", "+680", "+681", "+682", "+683", "+685", "+686", "+687", 
  "+688", "+689", "+690", "+691", "+692", "+850", "+852", "+853", "+855", "+856", "+880", 
  "+886", "+960", "+961", "+962", "+963", "+964", "+965", "+966", "+967", "+968", "+970", 
  "+971", "+972", "+973", "+974", "+975", "+976", "+977", "+992", "+993", "+994", "+995", 
  "+996", "+998"
];

export default function Contact() {
  const [form, setForm] = useState(initial);
  const [submitting, setSubmitting] = useState(false);

  const handleChange = (e) => {
    const { name, value } = e.target;
    setForm((f) => ({ ...f, [name]: value }));
  };

  const validate = () => {
    if (!form.name.trim()) return "Please share your name.";
    
    // Enhanced email validation
    const emailRegex = /^[a-zA-Z0-9._-]+@[a-zA-Z0-9.-]+\.[a-zA-Z]{2,}$/;
    if (!form.email.trim()) return "Email is required.";
    if (!emailRegex.test(form.email)) return "Please enter a valid email address.";
    
    // Phone validation
    if (!form.phone.trim()) return "Phone number is required.";
    if (!/^\d{6,15}$/.test(form.phone.replace(/[\s-]/g, ''))) {
      return "Please enter a valid phone number.";
    }
    
    if (!form.company.trim()) return "Which company are you with?";
    if (!form.message.trim()) return "Tell us a little about the project.";
    return null;
  };

  const onSubmit = async (e) => {
    e.preventDefault();
    const err = validate();
    if (err) { toast.error(err); return; }
    if (!ACCESS_KEY) {
      toast.error("Web3Forms key is missing. Please check your .env file.");
      console.error("ACCESS_KEY is undefined");
      return;
    }
    setSubmitting(true);
    try {
      const res = await fetch(WEB3FORMS_URL, {
        method: "POST",
        headers: { "Content-Type": "application/json", Accept: "application/json" },
        body: JSON.stringify({
          access_key: ACCESS_KEY,
          subject: `New inquiry from ${form.name} — SemiconOS`,
          from_name: "SemiconOS Website",
          name: form.name,
          email: form.email,
          phone: `${form.countryCode} ${form.phone}`,
          company: form.company,
          message: form.message,
          botcheck: form.website, // honeypot - must be empty
        }),
      });
      const data = await res.json();
      if (data.success) {
        toast.success("Sent. We'll get back within 24 hours.");
        setForm(initial);
      } else {
        toast.error(data.message || "Something went wrong. Please try again.");
      }
    } catch (e2) {
      toast.error("Something went wrong. Please try again.");
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <section id="contact" data-testid="contact-section" className="px-6 md:px-12 py-24 md:py-32">
      <div className="max-w-3xl mx-auto">
        <motion.div
          initial={{ opacity: 0, y: 18 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true, amount: 0.3 }}
          transition={{ duration: 0.7, ease: [0.16, 1, 0.3, 1] }}
        >
          <div className="overline mb-5" data-testid="contact-overline">Let's talk</div>
          <h2 className="font-display text-4xl md:text-5xl lg:text-[3.5rem] leading-[1.05] tracking-tight text-strong font-medium">
            Tell us what you're <span className="serif-italic text-accent">working on.</span>
          </h2>
          <p className="mt-6 text-base md:text-lg text-muted leading-relaxed max-w-xl">
            A short note is enough. We reply within 24 hours, usually with a question or two.
          </p>
        </motion.div>

        <motion.form
          onSubmit={onSubmit}
          noValidate
          data-testid="contact-form"
          initial={{ opacity: 0, y: 18 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true, amount: 0.2 }}
          transition={{ duration: 0.7, delay: 0.1, ease: [0.16, 1, 0.3, 1] }}
          className="mt-14 space-y-10"
        >
          <div className="grid grid-cols-1 md:grid-cols-2 gap-x-10 gap-y-8">
            {/* Honeypot */}
            <input 
              name="botcheck" 
              type="text" 
              tabIndex={-1} 
              autoComplete="off" 
              value={form.website} 
              onChange={handleChange}
              style={{ position: "absolute", left: "-10000px", width: "1px", height: "1px", overflow: "hidden" }}
              aria-hidden="true"
            />
            <div>
              <label htmlFor="name" className="field-label block mb-2">Name</label>
              <input id="name" name="name" type="text" autoComplete="name" value={form.name} onChange={handleChange} className="field-input" placeholder="" data-testid="contact-input-name" disabled={submitting} />
            </div>
            <div>
              <label htmlFor="email" className="field-label block mb-2">Email</label>
              <input id="email" name="email" type="email" autoComplete="email" value={form.email} onChange={handleChange} className="field-input" placeholder="" data-testid="contact-input-email" disabled={submitting} />
            </div>
            <div className="md:col-span-2">
              <label htmlFor="phone" className="field-label block mb-2">Phone</label>
              <div className="flex gap-3">
                <select 
                  name="countryCode" 
                  value={form.countryCode} 
                  onChange={handleChange} 
                  className="field-input" 
                  style={{ 
                    width: '90px',
                    paddingRight: '1.5rem',
                    backgroundImage: 'url("data:image/svg+xml,%3Csvg xmlns=\'http://www.w3.org/2000/svg\' width=\'12\' height=\'12\' viewBox=\'0 0 12 12\'%3E%3Cpath fill=\'%23666\' d=\'M6 9L1 4h10z\'/%3E%3C/svg%3E")',
                    backgroundRepeat: 'no-repeat',
                    backgroundPosition: 'right 0.5rem center',
                    appearance: 'none',
                    cursor: 'pointer'
                  }}
                  disabled={submitting}
                  data-testid="contact-input-country-code"
                >
                  {COUNTRY_CODES.map((code) => (
                    <option key={code} value={code}>
                      {code}
                    </option>
                  ))}
                </select>
                <input 
                  id="phone" 
                  name="phone" 
                  type="tel" 
                  autoComplete="tel" 
                  value={form.phone} 
                  onChange={handleChange} 
                  className="field-input" 
                  style={{ flex: 1 }}
                  placeholder="" 
                  data-testid="contact-input-phone" 
                  disabled={submitting} 
                />
              </div>
            </div>
            <div className="md:col-span-2">
              <label htmlFor="company" className="field-label block mb-2">Company</label>
              <input id="company" name="company" type="text" autoComplete="organization" value={form.company} onChange={handleChange} className="field-input" placeholder="" data-testid="contact-input-company" disabled={submitting} />
            </div>
          </div>
          <div>
            <label htmlFor="message" className="field-label block mb-2">Message</label>
            <textarea id="message" name="message" rows={4} value={form.message} onChange={handleChange} className="field-input" placeholder="" data-testid="contact-input-message" disabled={submitting} />
          </div>
          <div className="pt-2">
            <button type="submit" className="btn-primary" data-testid="contact-submit-button" disabled={submitting}>
              {submitting ? "Sending…" : "Send inquiry"} <span aria-hidden>→</span>
            </button>
          </div>
        </motion.form>
      </div>
    </section>
  );
}
